<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import { useCommonStore } from '@/shared/store'
  import { CONTACTS_KEYS } from '@/shared/types/common.types'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import HotlineButtonQC from '@/entities/Flow/components/HotlineButtonQC/HotlineButtonQC.vue'
  import WhatsAppButtonQC from '@/entities/Flow/components/WhatsAppButtonQC/WhatsAppButtonQC.vue'

  interface Emits {
    (e: 'resolve'): void
  }

  interface Props {
    homeUrl: string
  }

  defineProps<Props>()
  const emit = defineEmits<Emits>()

  const commonStore = useCommonStore()

  const contacts = computed(() => ({
    phoneLink: commonStore.getContactValue(CONTACTS_KEYS.PHONE_LINK),
    phoneText: commonStore.getContactValue(CONTACTS_KEYS.PHONE_LINK_TEXT),
    waLink: commonStore.getContactValue(CONTACTS_KEYS.WA_LINK_GENERAL),
  }))

  onMounted(() => commonStore.getContacts())
</script>

<template>
  <div class="ds-network-error network-error">
    <div class="network-error__wrapper" direction="column">
      <div class="network-error__img">
        <img src="@/shared/assets/images/network-error.png" alt="network-error" />
      </div>
      <WrapperQC direction="column" gap="var(--spacing-5)">
        <h1 class="network-error__message-title">
          {{ $t('travel.common.failed.heading') }}
        </h1>
        <p class="network-error__message-desc">
          {{ $t('travel.common.failed.text') }}
        </p>
      </WrapperQC>
      <WrapperQC class="network-error__help-wrapper" direction="column" gap="var(--spacing-5)">
        <ButtonQC wide data-test="reload-button" @click="emit('resolve')">
          {{ $t('qic_error_try_again') }}
        </ButtonQC>
        <ButtonQC wide variant="outline" data-test="go-home-button" :href="homeUrl">
          {{ $t('qic_error_go_home') }}
        </ButtonQC>
      </WrapperQC>

      <WrapperQC class="network-error__help" direction="column" gap="var(--spacing-7)">
        <WrapperQC class="network-error__help-wrapper" direction="column" gap="var(--spacing-5)">
          <p class="network-error__help-info">
            {{ $t('common.get_in_touch') }}
          </p>
          <p class="network-error__help-contact">
            {{ $t('common.availabilty_hours') }}
          </p>
        </WrapperQC>
        <WrapperQC class="network-error__help-wrapper" direction="column" gap="var(--spacing-5)">
          <WhatsAppButtonQC
            :loading="!contacts.waLink"
            :href="contacts.waLink"
            data-test="network-error-whatsapp-button"
          />
          <HotlineButtonQC
            :loading="!contacts.phoneLink && !contacts.phoneText"
            :phone="contacts.phoneText"
            :link="contacts.phoneLink"
          />
        </WrapperQC>
      </WrapperQC>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @use '@/shared/assets/styles/mixins';
  @use '@qicgroupdoha/qic-connect-ds/scss/typography';

  .network-error {
    width: 100%;
    max-width: 544px;
    margin: 0 auto;
    padding: var(--spacing-10) var(--spacing-8);
    border-radius: var(--radius-8);
    background: var(--colorBgSrfPrimary);

    @include mixins.mobile-flow {
      padding: var(--spacing-10) var(--spacing-5) var(--spacing-8);
    }
  }

  .network-error__wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-8);

    @include mixins.mobile-flow {
      gap: var(--spacing-7);
    }
  }

  .network-error__img {
    margin: 0 auto;
    width: 120px;
    height: 120px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .network-error__message-title {
    @include typography.heading-2;

    text-align: center;

    @include mixins.mobile-flow {
      @include typography.heading-3;
    }
  }

  .network-error__message-desc {
    @include typography.text-regular;

    color: var(--colorTxtSrfSecondary);
    text-align: center;
  }

  .network-error__help {
    align-items: center;
    padding-block: var(--spacing-8);
    border-top: 1px solid var(--colorBrdrSrfPrimary);

    @include mixins.mobile-flow {
      padding-block: var(--spacing-7);
    }
  }

  .network-error__help-wrapper {
    width: 100%;
    align-items: center;
  }

  .network-error__help-info {
    @include typography.heading-4;

    @include mixins.mobile-flow {
      @include typography.heading-5;
    }
  }

  .network-error__help-contact {
    @include typography.text-regular;
  }
</style>
